import React, { useState } from 'react';
import { Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import BaseModal from './BaseModal';
import { LoadingButton } from './LoadingStates';
import { sanitizeEmail, sanitizeText } from '../utils/security';
import { VALIDATION_RULES, CSS_CLASSES } from '../utils/constants';

const AuthModal = () => {
  const {
    authForm,
    setAuthForm,
    authMode,
    showAuthModal,
    handleLogin,
    handleSignup,
    handlePasswordReset,
    closeAuthModal,
    switchAuthMode,
  } = useAuth();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Simple validation function
  const validateAuth = formData => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (authMode !== 'reset') {
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters';
      }
    }

    if (authMode === 'signup' && formData.confirmPassword !== formData.password) {
      newErrors.confirmPassword = "Passwords don't match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!validateAuth(authForm)) {
      return;
    }

    setIsSubmitting(true);
    try {
      if (authMode === 'login') {
        await handleLogin(e);
      } else if (authMode === 'signup') {
        const result = await handleSignup(e);
        if (result.success) {
          setSuccessMessage(
            "Account created! Please check your email to confirm, then create profile information on first login. Don't forget to look in your spam!"
          );
          setShowSuccessDialog(true);
        }
      } else if (authMode === 'reset') {
        const result = await handlePasswordReset(authForm.email);
        if (result.success) {
          switchAuthMode('login');
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <BaseModal
        isOpen={showAuthModal}
        onClose={closeAuthModal}
        title={
          authMode === 'login'
            ? 'Login to Rewind Archive'
            : authMode === 'signup'
              ? 'Create Account'
              : 'Reset Password'
        }
        maxWidth="md"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email Field - moved to bottom */}
          <div>
            <input
              type="email"
              placeholder="Email"
              value={authForm.email}
              onChange={e => {
                const sanitizedEmail = sanitizeEmail(e.target.value);
                setAuthForm({ ...authForm, email: sanitizedEmail });
                if (errors.email) setErrors({ ...errors, email: '' });
              }}
              className={`${CSS_CLASSES.INPUT_STANDARD} bg-black text-white border-gray-600 hover:border-red-500 placeholder-gray-400 disabled:bg-black disabled:text-white disabled:border-gray-600 ${errors.email ? 'border-red-500' : ''}`}
              autoComplete="email"
              disabled={false}
              readOnly={false}
            />
            {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
          </div>

          {authMode !== 'reset' && (
            <div>
              <input
                type="password"
                placeholder="Password"
                value={authForm.password}
                onChange={e => {
                  const sanitizedPassword = sanitizeText(e.target.value).substring(
                    0,
                    VALIDATION_RULES.PASSWORD_MAX_LENGTH
                  );
                  setAuthForm({ ...authForm, password: sanitizedPassword });
                  if (errors.password) setErrors({ ...errors, password: '' });
                }}
                className={`${CSS_CLASSES.INPUT_STANDARD} bg-black text-white border-gray-600 hover:border-red-500 placeholder-gray-400 ${errors.password ? 'border-red-500' : ''}`}
                autoComplete="current-password"
                disabled={false}
                readOnly={false}
              />
              {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
            </div>
          )}

          {authMode === 'signup' && (
            <div>
              <input
                type="password"
                placeholder="Confirm Password"
                value={authForm.confirmPassword}
                onChange={e => {
                  const sanitizedPassword = sanitizeText(e.target.value).substring(
                    0,
                    VALIDATION_RULES.PASSWORD_MAX_LENGTH
                  );
                  setAuthForm({ ...authForm, confirmPassword: sanitizedPassword });
                  if (errors.confirmPassword) setErrors({ ...errors, confirmPassword: '' });
                }}
                className={`${CSS_CLASSES.INPUT_STANDARD} bg-black text-white border-gray-600 hover:border-red-500 placeholder-gray-400 ${
                  errors.confirmPassword ? 'border-red-500' : ''
                }`}
                autoComplete="new-password"
                disabled={false}
                readOnly={false}
              />
              {errors.confirmPassword && (
                <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>
              )}
            </div>
          )}

          {authMode === 'login' && (
            <div className="flex justify-between items-center">
              <div className="text-gray-500 text-sm">
                Don't have an account?{' '}
                <button
                  type="button"
                  onClick={() => switchAuthMode('signup')}
                  className="text-red-500 hover:text-red-400 underline"
                >
                  Sign up
                </button>
              </div>
              <div className="text-gray-500 text-sm">
                <button
                  type="button"
                  onClick={() => switchAuthMode('reset')}
                  className="text-red-500 hover:text-red-400 underline"
                >
                  Forgot password?
                </button>
              </div>
            </div>
          )}

          {authMode === 'signup' && (
            <div className="flex justify-between items-center">
              <div className="text-gray-500 text-sm">
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => switchAuthMode('login')}
                  className="text-red-500 hover:text-red-400 underline"
                >
                  Login
                </button>
              </div>
            </div>
          )}

          {authMode === 'reset' && (
            <div className="flex justify-center">
              <div className="text-gray-500 text-sm text-center">
                <div className="mb-2">
                  Enter your email address and we'll send you a password reset link.
                </div>
                <button
                  type="button"
                  onClick={() => switchAuthMode('login')}
                  className="text-red-500 hover:text-red-400 underline"
                >
                  Back to login
                </button>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={closeAuthModal}
              className="px-4 py-2 bg-black text-white border border-gray-600 rounded hover:border-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <LoadingButton
              type="submit"
              isLoading={isSubmitting}
              disabled={
                !authForm.email ||
                (authMode !== 'reset' && !authForm.password) ||
                (authMode === 'signup' && !authForm.confirmPassword)
              }
              className={CSS_CLASSES.BUTTON_PRIMARY}
            >
              {authMode === 'login'
                ? 'Login'
                : authMode === 'signup'
                  ? 'Sign Up'
                  : 'Send Reset Email'}
            </LoadingButton>
          </div>
        </form>
      </BaseModal>

      {/* Success Dialog */}
      {showSuccessDialog && (
        <BaseModal
          isOpen={showSuccessDialog}
          onClose={() => {
            setShowSuccessDialog(false);
            closeAuthModal();
          }}
          title="Account Created Successfully!"
          maxWidth="sm"
          showCloseButton={false}
        >
          <div className="text-center p-4">
            <div className="mb-4">
              <Check className="mx-auto h-16 w-16 text-green-500" />
            </div>
            <p className="text-white text-lg mb-6">{successMessage}</p>
            <button
              onClick={() => {
                setShowSuccessDialog(false);
                closeAuthModal();
              }}
              className="bg-black text-red-500 px-6 py-2 rounded-lg border border-red-500 hover:bg-red-500 hover:text-black transition-colors"
            >
              Got it!
            </button>
          </div>
        </BaseModal>
      )}
    </>
  );
};

export default AuthModal;
